import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { localeForCurrency } from "../utils/currencyLocale"
import { usesIndianGrouping } from '../utils/currencyLocale'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency with proper symbol. Zero (and nullish) values drop the symbol
// — "$0.00" is visually noisy, especially in dense tables/exports.
export function formatCurrency(amount: number, currency: string = 'USD', showDecimals: boolean = false): string {
  const decimals = showDecimals ? 2 : 0
  // INR groups in lakhs/crores; everything else keeps en-US as before.
  const locale = localeForCurrency(currency, 'en-US')
  if (!amount) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(0)
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

// Format date consistently
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
}

// Format date with time
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

// Format percentage
export function formatPercentage(value: number): string {
  return `${Math.round(value)}%`
}

// Compact number for chart axes: 3 → "3", 3_000 → "3K", 3_100_000 → "3.1M", 3_000_000 → "3M"
// CR-4352: Strip trailing ".0" so axis labels read "3M" not "3.0M"
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value)
  const format = (n: number) => {
    const s = n.toFixed(1)
    return s.endsWith('.0') ? s.slice(0, -2) : s
  }
  if (abs >= 1_000_000) return `${format(value / 1_000_000)}M`
  if (abs >= 1_000) return `${format(value / 1_000)}K`
  return format(value)
}

// Compact number for MONEY axes, in the currency's own convention.
// INR groups in lakhs/crores — 1_20_000 → "1.2L", 3_40_00_000 → "3.4Cr" — an
// Indian reader does not think in "34M". Every other currency keeps K/M.
export function formatCompactCurrencyNumber(value: number, currency?: string): string {
  if (!usesIndianGrouping(currency)) return formatCompactNumber(value)
  const abs = Math.abs(value)
  const format = (n: number) => {
    const s = n.toFixed(1)
    return s.endsWith('.0') ? s.slice(0, -2) : s
  }
  if (abs >= 10_000_000) return `${format(value / 10_000_000)}Cr`
  if (abs >= 100_000) return `${format(value / 100_000)}L`
  if (abs >= 1_000) return `${format(value / 1_000)}K`
  return format(value)
}

// Get initials from name
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
